<template>
  <div :class="[$globalStyle.header, $globalStyle.hostHeader]">
    <template v-if="isMobile">
      <HeaderItemButton
        :icon-link="headerMenuIcon"
        :icon-only="true"
        :clickFunction="toggleMobileHeader"
        backBg="#333"
        frontBg="#676767"
        :button-width="'48px'"
      />

      <HostMobileHeader
        v-model="isMobileHeaderOpen"
        :user="user"
        :phase="phase"
        :reading-time="readingTime"
        :discussion-time="discussionTime"
        :join-code="joinCode"
        :real-text-label="realTextLabel"
        :groups="groups"
        :host="host"
        :is-moderator="isModerator"
        :game-winner="gameWinner"
        :show-group-saboteurs="canShowSaboteurs ? handleShowSaboteursMobile : null"
        :show-group-winners="canShowWinners ? handleShowWinnersMobile : null"
      />

      <HeaderLanguageSelector />
    </template>

    <template v-else>
      <div :class="$globalStyle.headerLeft">
        <div v-if="displayUserName" :class="$globalStyle.headerItem">
          <div :class="$globalStyle.headerItemLabel">{{ $t('name') }}</div>
          <div :class="[$globalStyle.headerItemText]">
            {{ displayUserName }}
          </div>
        </div>
        <div v-if="isModerator && host?.name" :class="$globalStyle.headerItem">
            <div :class="$globalStyle.headerItemLabel">
              {{ $t("host-name") }}
            </div>
            <div :class="$globalStyle.headerItemText">
              {{ host.name }}
            </div>
          </div>
        <!-- <div v-if="allowedPlayers" :class="$globalStyle.headerItem">
          <div :class="$globalStyle.headerItemLabel">Allowed Players</div>
          <div :class="[$globalStyle.headerItemText]">
            {{ allowedPlayers }}
          </div>
        </div> -->
        <template v-if="['JOIN'].includes(phase)">
          <div v-if="hasReadingTime" :class="$globalStyle.headerItem">
            <div :class="$globalStyle.headerItemLabel">{{ $t('reading-time') }}</div>
            <div :class="[$globalStyle.headerItemText]">
              {{ readingTime }}
              <span :class="$globalStyle.headerItemSubText">{{ $t('minutes') }}</span>
            </div>
          </div>
          <div v-if="hasDiscussionTime" :class="$globalStyle.headerItem">
            <div :class="$globalStyle.headerItemLabel">{{ $t('discussion-time') }}</div>
            <div :class="[$globalStyle.headerItemText]">
              {{ discussionTime }}
              <span :class="$globalStyle.headerItemSubText">{{ $t('minutes') }}</span>
            </div>
          </div>
        </template>

        <template v-else>
          <div v-if="hasJoinCode" :class="$globalStyle.headerItem">
            <div :class="$globalStyle.headerItemLabel">{{ $t('join-code') }}</div>
            <div :class="[$globalStyle.headerItemText]">
              {{ joinCode }}
            </div>
          </div>
        </template>

        <div
          v-if="hasRealTextLabel || canToggleOriginalText"
          :class="[$globalStyle.headerItem, $style.originalTextRow]"
        >
          <div>
            <div :class="$globalStyle.headerItemLabel">{{ $t('orig-text') }}</div>
            <div
              :class="[
                $globalStyle.headerItemText,
                $globalStyle.headerItemHostSaboteurText,
                canToggleOriginalText &&
                  !hostShowSaboteurText &&
                  $globalStyle.blurredSaboteurText,
              ]"
            >
              {{ $t("text") }}
              {{
                canToggleOriginalText
                  ? hostShowSaboteurText
                    ? realTextLabel || "—"
                    : "X"
                  : realTextLabel
              }}
            </div>
          </div>
          <HeaderItemButton
            v-if="canToggleOriginalText"
            :text="$t('show-saboteur-text')"
            :aria-label="
              hostShowSaboteurText ? 'Hide original text' : 'Show original text'
            "
            :iconLink="hostShowSaboteurText ? secretVisibleIcon : secretHideIcon"
            :clickFunction="toggleHostShowSaboteurText"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :icon-only="true"
            :button-width="'36px'"
            :button-height="'28px'"
          />
        </div>

        <div v-if="canShowSaboteurs" :class="$globalStyle.headerItem">
          <HeaderItemButton
            :text="$t('show-saboteurs')"
            :clickFunction="showGroupSaboteurs"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :button-width="actionButtonWidth"
          />
        </div>

        <div v-if="canShowWinners" :class="$globalStyle.headerItem">
          <HeaderItemButton
            :text="$t('show-winners')"
            :clickFunction="showGroupWinners"
            backBg="#000000"
            frontBg="#676767"
            textColor="#ffc642"
            :button-width="actionButtonWidth"
          />
        </div>
      </div>

      <div :class="$globalStyle.headerRight">
        <div v-if="gameWinner" :class="$globalStyle.headerItem">
          <div :class="$globalStyle.headerItemLabel">{{ gameWinner === "None" ? $t('result') : $t('game-winner') }}</div>
          <div :class="[$globalStyle.headerItemText, $globalStyle.headerItemHostWinnerText]">
            {{ gameWinner === "None" 
                ? $t("game-tied") 
                : gameWinner === "Players" 
                  ? $t("players") 
                  : gameWinner === "Saboteur" 
                    ? $t("saboteur") 
                    : gameWinner 
            }}
          </div>
        </div>

        <div :class="$globalStyle.headerHostChip">
          <img :src="detectiveHatIcon" alt="Header" :class="$globalStyle.headerHostChipIcon" />
          <div :class="$globalStyle.headerHostChipText">{{ roleChipText }}</div>
        </div>
        <div :class="$globalStyle.headerItem">
            <HeaderLanguageSelector />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import globalStyles from "@/css/global.module.css";
import DetectiveHatIcon from "@/assets/icons/detective-hat.svg";
import HeaderLanguageSelector from "../util/HeaderLanguageSelector.vue";
import HeaderItemButton from "../buttons/HeaderItemButton.vue";
import useDeviceType from "@/utils/useDeviceType";
import HeaderMenuIcon from "@/assets/icons/header-menu-white.svg";
import HostMobileHeader from "./HostMobileHeader.vue";
import SecretVisibleIcon from "@/assets/icons/secret-visible.svg";
import SecretHideIcon from "@/assets/icons/secret-hide.svg";
import { useGameStore } from "@/stores/gameStore";
import styles from "@/css/HostHeaderBox.module.css";

const device = useDeviceType();

export default {
  name: "HostHeaderBox",
  components: {
    HeaderItemButton,
    HeaderLanguageSelector,
    HostMobileHeader,
  },
  props: {
    user: {
      type: Object,
      required: true,
    },
    joinCode: {
      type: [String, Number],
      required: false,
      default: "",
    },
    readingTime: {
      type: Number,
      required: false,
      default: null,
    },
    discussionTime: {
      type: Number,
      required: false,
      default: null,
    },
    realTextLabel: {
      type: String,
      required: false,
      default: "",
    },
    // startGameAction: {
    //   type: Function,
    //   required: false,
    // },
    // goHomeAction: {
    //   type: Function,
    //   required: false,
    // },
    // saboteur: {
    //   type: Object,
    //   required: false,
    //   default: () => {},
    // },
    gameWinner: {
      type: String,
      required: false,
      default: "",
    },
    openSaboteursModal: {
      type: Function,
      required: false,
      default: null,
    },
    openWinnersModal: {
      type: Function,
      required: false,
      default: null,
    },
    groups: {
      type: Array,
      required: false,
      default: () => [],
    },
    phase: {
      type: String,
      required: false,
      default: "",
    },
    isModerator: {
      type: Boolean,
      required: false,
      default: false,
    },
    host: { 
      type: Object, 
      default: null 
    }
  },
  data() {
    return {
      detectiveHatIcon: DetectiveHatIcon,
      headerMenuIcon: HeaderMenuIcon,
      isMobileHeaderOpen: false,
      secretVisibleIcon: SecretVisibleIcon,
      secretHideIcon: SecretHideIcon,
    };
  },
  computed: {
    $style() {
      return styles;
    },
    $globalStyle() {
      return globalStyles;
    },
    hostShowSaboteurText() {
      const store = useGameStore();
      return this.isModerator
        ? store.moderatorShowSaboteurText
        : store.hostShowSaboteurText;
    },
    canToggleOriginalText() {
      return !!this.phase;
    },
    width() {
      return device.width.value;
    },

    height() {
      return device.height.value;
    },

    isMobile() {
      return device.isMobile.value;
    },

    isTablet() {
      return device.isTablet.value;
    },

    isDesktop() {
      return device.isDesktop.value;
    },

    displayUserName() {
      return this.user?.name || "";
    },

    hasJoinCode() {
      return this.joinCode !== null && this.joinCode !== undefined && this.joinCode !== "";
    },

    hasReadingTime() {
      return this.readingTime !== null && this.readingTime !== undefined;
    },

    hasDiscussionTime() {
      return this.discussionTime !== null && this.discussionTime !== undefined;
    },

    hasRealTextLabel() {
      return (
        this.realTextLabel !== null &&
        this.realTextLabel !== undefined &&
        this.realTextLabel !== ""
      );
    },

    showSetupTimes() {
      return ["JOIN"].includes(this.phase);
    },

    roleChipText() {
      return this.isModerator ? this.$t('moderator') : this.$t('host');
    },

    canShowSaboteurs() {
      return Array.isArray(this.groups) && this.groups.length > 0;
    },

    canShowWinners() {
      return (
        Array.isArray(this.groups) &&
        this.groups.length > 0 &&
        ["REVEAL", "DEBRIEF"].includes(this.phase)
      );
    },

    actionButtonWidth() {
      if (this.isMobile) return "64px";
      if (this.isTablet) return "80px";
      return "100px";
    },
  },
  methods: {
    showGroupSaboteurs() {
      if (typeof this.openSaboteursModal === "function") {
        this.openSaboteursModal();
      }
    },

    showGroupWinners() {
      if (typeof this.openWinnersModal === "function") {
        this.openWinnersModal();
      }
    },

    handleShowSaboteursMobile() {
      this.toggleMobileHeader();
      this.showGroupSaboteurs();
    },

    handleShowWinnersMobile() {
      this.toggleMobileHeader();
      this.showGroupWinners();
    },

    toggleMobileHeader() {
      this.isMobileHeaderOpen = !this.isMobileHeaderOpen;
    },
    toggleHostShowSaboteurText() {
      if (this.isModerator) {
        useGameStore().toggleModeratorShowSaboteurText();
      } else {
        useGameStore().toggleHostShowSaboteurText();
      }
    },
  },
};
</script>

<style scoped></style>